import { useMemo, useState, useEffect, useCallback } from 'react';

import { useJsrClient } from 'src/hooks/use-jsr-client';

import { logger } from 'src/utils/logger';

import { toast } from 'src/components/snackbar';

import { pruneEmptyFolders, isUserFolderReport } from './report-tree-utils';


// =====================================================================

export function useListHandler(searchQuery = '') {
    const {
        isJsrAvailable,
        getResources,
        getFavorites,
        setFavorites: setJsrFavorites,
        deleteFavorites: deleteJsrFavorites,
        deleteResource,
    } = useJsrClient();

    const [allReports, setAllReports] = useState([]);
    const [favorites, setFavorites] = useState(new Set());
    const [loading, setLoading] = useState(true);

    // Load reports from Jasper
    useEffect(() => {
        loadReports();
    }, [isJsrAvailable]);

    useEffect(() => {
        const loadFavorites = async () => {
            try {
                const favs = await getFavorites();
                setFavorites(new Set(favs.map(fav => fav.uri)));
            } catch (e) {
                logger.error('Error loading favorites:', e);
            }
        };

        if (isJsrAvailable) {
            loadFavorites();
        }
    }, [isJsrAvailable]);

    const loadReports = useCallback(() => {

        setLoading(true);

        const successCallback = (data) => {
            const scopedData = data.filter(
                (resource) => resource.uri && isAllowedJsrPath(resource.uri),
            );

            // Organize reports by folder structure
            const reportsByFolder = {};

            scopedData.forEach((resource) => {
                if (!['reportUnit', 'dashboard', 'folder'].includes(resource.resourceType)) {
                    return;
                }

                // Folders are part of the tree structure only, not table report rows.
                if (resource.resourceType === 'folder') {
                    const folderUri = normalizeResourceUri(resource.uri);
                    if (!reportsByFolder[folderUri]) {
                        reportsByFolder[folderUri] = [];
                    }
                    return;
                }

                const folderPath = extractFolderPath(resource.uri);

                if (!reportsByFolder[folderPath]) {
                    reportsByFolder[folderPath] = [];
                }

                reportsByFolder[folderPath].push({
                    uri: resource.uri,
                    label: resource.label,
                    description: resource.description,
                    resourceType: resource.resourceType,
                    folderPath,
                });
            });

            const folderLabelsByUri = buildFolderLabelsByUri(scopedData);

            // Convert to hierarchical structure
            const hierarchicalReports = createHierarchy(reportsByFolder, folderLabelsByUri);
            setAllReports(hierarchicalReports);
            setLoading(false);
        }

        getResources().then(successCallback).catch((e) => {
            setLoading(false);
        });

    }, []);

    const toggleFavorite = useCallback(async (uri) => {
        const updatedFavorites = new Set(favorites);

        if (favorites.has(uri)) {
            updatedFavorites.delete(uri);

            try {
                await deleteJsrFavorites([uri]);
            } catch (e) {
                logger.error('Error removing favorite:', e);
            }
        } else {
            updatedFavorites.add(uri);

            try {
                await setJsrFavorites([uri]);
            } catch (e) {
                logger.error('Error adding favorite:', e);
            }
        }

        setFavorites(updatedFavorites);
    }, [favorites, setJsrFavorites, deleteJsrFavorites]);

    const deleteReport = useCallback(async (uri) => {
        if (!isUserFolderReport(uri)) {
            toast.error('Only reports in User Folders can be deleted');
            return false;
        }

        try {
            await deleteResource(uri);

            if (favorites.has(uri)) {
                try {
                    await deleteJsrFavorites([uri]);
                } catch (e) {
                    logger.error('Error removing favorite after delete:', e);
                }

                setFavorites((prev) => {
                    const next = new Set(prev);
                    next.delete(uri);
                    return next;
                });
            }

            loadReports();
            toast.success('Report deleted successfully');
            return true;
        } catch (e) {
            logger.error('Error deleting report:', e);

            const status = e?.response?.status;
            if (status === 401 || status === 403) {
                toast.error('You do not have permission to delete this report');
            } else {
                toast.error('Failed to delete report');
            }

            return false;
        }
    }, [deleteResource, deleteJsrFavorites, favorites, loadReports]);

    // Filter and rebuild hierarchy based on search query
    const reports = useMemo(() => {
        if (!searchQuery.trim()) {
            return allReports;
        }

        const query = searchQuery.toLowerCase();
        return filterHierarchy(allReports, query);
    }, [allReports, searchQuery]);

    return {
        reports,
        favorites,
        loading,
        toggleFavorite,
        deleteReport,
    };
}

// =====================================================================
// Helpers
// =====================================================================

/**
 * Extract folder path from URI
 * @param {string} uri - Full URI path
 * @returns {string} Folder path
 */
function extractFolderPath(uri) {
    // Remove file name from URI
    const parts = uri.split('/');
    return parts.slice(0, -1).join('/');
}

/**
 * Normalize JSR resource URIs for consistent map lookups (leading slash, collapse slashes).
 * @param {string} uri
 * @returns {string}
 */
function normalizeResourceUri(uri) {
    if (!uri || typeof uri !== 'string') {
        return '';
    }
    let s = uri.trim();
    if (!s.startsWith('/')) {
        s = `/${s}`;
    }
    return s.replace(/\/{2,}/g, '/') || '/';
}

/** Only these JSR repository roots appear in the report list tree */
const ALLOWED_JSR_ROOT_PATHS = ['/Reports', '/userFolders'];

/**
 * @param {string} uri
 * @returns {boolean}
 */
function isAllowedJsrPath(uri) {
    const n = normalizeResourceUri(uri);
    return ALLOWED_JSR_ROOT_PATHS.some((root) => n === root || n.startsWith(`${root}/`));
}

/**
 * Build folder URI → display label from JSR resourceLookup folder entries.
 * @param {Array} data - resourceLookup array
 * @returns {Record<string, string>}
 */
function buildFolderLabelsByUri(data) {
    /** @type {Record<string, string>} */
    const map = {};
    data.forEach((resource) => {
        if (resource.resourceType !== 'folder' || !resource.uri) {
            return;
        }
        const label = typeof resource.label === 'string' ? resource.label.trim() : '';
        if (!label) {
            return;
        }
        map[normalizeResourceUri(resource.uri)] = label;
    });
    return map;
}

/**
 * Create hierarchical structure from flat folder structure
 * @param {Object} reportsByFolder - Reports organized by folder
 * @param {Record<string, string>} folderLabelsByUri - JSR folder URI → label
 * @returns {Array} Hierarchical report structure
 */
function createHierarchy(reportsByFolder, folderLabelsByUri = /** @type {Record<string, string>} */ ({})) {
    const hierarchy = [];
    const folderMap = {};

    // Create folder entries
    Object.entries(reportsByFolder).forEach(([folderPath, reports]) => {
        const folderParts = folderPath.split('/').filter(Boolean);
        let currentPath = '';

        folderParts.forEach((part, index) => {
            currentPath += `/${part}`;
            const normalizedPath = normalizeResourceUri(currentPath);
            const displayLabel = folderLabelsByUri[normalizedPath] || part;

            if (!folderMap[currentPath]) {
                folderMap[currentPath] = {
                    name: displayLabel,
                    label: displayLabel,
                    path: currentPath,
                    children: [],
                    expanded: false
                };

                if (index === 0) {
                    hierarchy.push(folderMap[currentPath]);
                } else {
                    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
                    if (folderMap[parentPath]) {
                        folderMap[parentPath].children.push(folderMap[currentPath]);
                    }
                }
            }
        });

        // Add reports to the leaf folder
        const leafFolder = folderMap[folderPath];
        if (leafFolder) {
            leafFolder.reports = reports;
        }
    });

    // Sort subcategories alphabetically by label at every level
    const sortFolders = (folders) => {
        folders.sort((a, b) => a.label.localeCompare(b.label));
        folders.forEach((folder) => {
            if (folder.children?.length) {
                sortFolders(folder.children);
            }
        });
    };

    sortFolders(hierarchy);

    return pruneEmptyFolders(hierarchy);
}

/**
 * Filter hierarchy based on search query
 * @param {Array} hierarchy - Full hierarchy
 * @param {string} query - Search query (lowercase)
 * @returns {Array} Filtered hierarchy with matching reports and their parent folders
 */
function filterHierarchy(hierarchy, query) {
    return hierarchy
        .map((folder) => filterFolder(folder, query))
        .filter((folder) => folder !== null);
}

/**
 * Recursively filter a folder and its children
 * @param {Object} folder - Folder to filter
 * @param {string} query - Search query
 * @returns {Object|null} Filtered folder or null if no matches
 */
function filterFolder(folder, query) {
    // Filter reports in this folder
    const filteredReports = (folder.reports || []).filter((report) =>
        report.label.toLowerCase().includes(query) ||
        report.description.toLowerCase().includes(query)
    );

    // Recursively filter child folders
    const filteredChildren = (folder.children || [])
        .map((child) => filterFolder(child, query))
        .filter((child) => child !== null);

    // Return null if no matches in this folder or its children
    if (filteredReports.length === 0 && filteredChildren.length === 0) {
        return null;
    }

    // Return filtered folder with matching reports and children
    return {
        ...folder,
        reports: filteredReports,
        children: filteredChildren,
        expanded: true, // Auto-expand filtered folders for better visibility
    };
}
